import * as React from "react";
import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import authStore from "../../../../stores/AuthStore";
import { User } from "../../../../stores/AuthStore/types";
import {
  Button,
  CircularProgress,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import useTranslation from "../../../../hooks/useTranslation";
import feedbackStore from "../../../../stores/FeedbackStore";
import { FeedbackSeverity } from "../../../../stores/FeedbackStore/types";
import TableHead from "@mui/material/TableHead";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Dialog from "@mui/material/Dialog";
import { barList } from "../../../Profile/helper";

export default function PendingRegistrations() {
  const { t } = useTranslation();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<User[]>([]);
  const [reasonDialogOpen, setReasonDialogOpen] = useState(false);
  const [rejectedUser, setRejectedUser] = useState<User | undefined>();

  useEffect(() => {
    setLoading(true);
    authStore
      .fetchPendingRegistrations()
      .then((result) => setRows(result))
      .catch(() => {
        feedbackStore.setShowFeedback(true, {
          severity: FeedbackSeverity.ERROR,
          message: "connection-error",
        });
      })
      .finally(() => setLoading(false));
  }, []);

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAction = (
    user: User | undefined,
    method: "approve" | "reject"
  ) => {
    user = user || rejectedUser;

    if (user?.id) {
      setLoading(true);
      (method === "approve"
        ? authStore.approveLawyerApplication(user)
        : authStore.rejectLawyerApplication(user)
      )
        .then(() => {
          setRows((prev) => prev.filter((row) => user?.id !== row.id));
          feedbackStore.setShowFeedback(true, {
            severity: FeedbackSeverity.SUCCESS,
            message: "success",
          });
        })
        .catch(() => {
          feedbackStore.setShowFeedback(true, {
            message: t("error"),
            severity: FeedbackSeverity.ERROR,
          });
        })
        .finally(() => setLoading(false));
    }
  };

  const handleOnClickReject = (user: User) => {
    setReasonDialogOpen(true);
    setRejectedUser(user);
  };

  /*
  const fetchUserPhotos = (user: User) => {
    if (userPhotosCache[user.id]) {
      setUserPhotos(userPhotosCache[user.id]);
    } else {
      documentStore.fetchDocumentsByUserId(user.id).then((docs) => {
        setUserPhotosCache((prev) => {
          return {
            ...prev,
            [user.id]: docs,
          };
        });
        setUserPhotos(docs);
      });
    }
  };
  */

  useEffect(() => {
    console.log(rejectedUser);
  }, [rejectedUser]);

  const RejectDialog = React.useCallback(
    () => (
      <Dialog
        open={reasonDialogOpen}
        onClose={() => setReasonDialogOpen(false)}
        className="adminRejectReasonDialog"
      >
        <DialogTitle>
          <Stack direction={"row"} justifyContent={"space-between"}>
            <div className={"mr pr"}>
              {t("reject")}: {rejectedUser?.id}
            </div>
            <Button
              size={"small"}
              variant={"text"}
              onClick={() => setReasonDialogOpen(false)}
              color={"secondary"}
              sx={{ minWidth: "36px !important" }}
            >
              &times;
            </Button>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ paddingTop: "16px !important" }}>
          <TextField
            fullWidth={true}
            rows={2}
            multiline={true}
            className={"pt pb pl pr mt"}
            label={t("reject-reason")}
            onChange={(e) => {
              setRejectedUser((prev) =>
                !!prev
                  ? {
                      ...prev,
                      reason: e.target.value,
                    }
                  : prev
              );
            }}
          />
        </DialogContent>
        <DialogActions className={"mr mb"}>
          <Button
            color={"error"}
            variant={"outlined"}
            onClick={() => {
              handleAction(undefined, "reject");
              setReasonDialogOpen(false);
            }}
          >
            {t("reject")}
          </Button>
        </DialogActions>
      </Dialog>
    ),
    [reasonDialogOpen, rejectedUser?.id]
  );

  /*
  const PreviewPhotosDialog = () => (
    <Dialog
      open={previewDialogOpen}
      onClose={() => setPreviewDialogOpen(false)}
      className="adminPreviewPhotosDialog"
    >
      <DialogTitle>
        <Stack direction={"row"} justifyContent={"space-between"}>
          <Button
            size={"small"}
            variant={"text"}
            onClick={() => setPreviewDialogOpen(false)}
            color={"secondary"}
            sx={{ minWidth: "36px !important", fontSize: "24px" }}
          >
            &times;
          </Button>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ paddingTop: "16px !important" }}>
        {userPhotos.map((photo) => (
          <img
            key={photo.id}
            src={photo.dataUrl}
            alt={photo.fileName}
            style={{ width: "100%" }}
            className="mb"
          />
        ))}
      </DialogContent>
    </Dialog>
  );
  */

  return (
    <Paper sx={{ position: "relative" }}>
      <TableContainer component={Paper}>
        {loading && (
          <div className="tableShadow">
            <CircularProgress />
          </div>
        )}
        <Table sx={{ minWidth: 500 }} aria-label="custom pagination table">
          <TableHead>
            <TableRow>
              <TableCell>{t("user-id")}</TableCell>
              <TableCell>
                {t("first-name")} {t("last-name")}
              </TableCell>
              <TableCell>{t("email")}</TableCell>
              <TableCell>{t("bar-no")}</TableCell>
              <TableCell>{t("bar-name")}</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(rowsPerPage > 0
              ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              : rows
            ).map((row) => (
              <TableRow key={row.id}>
                <TableCell component="th" scope="row">
                  {row.id}
                </TableCell>
                <TableCell>
                  {row.firstName} {row.lastName}
                </TableCell>
                <TableCell>{row.email}</TableCell>
                <TableCell>{row.barNo}</TableCell>
                <TableCell>
                  {barList.find((item) => item.value === row.barName)?.label} (
                  {row.barName})
                </TableCell>
                <TableCell align="right">
                  <Button
                    color={"error"}
                    onClick={() => handleOnClickReject(row)}
                    disabled={loading}
                  >
                    {t("reject")}
                  </Button>
                  <Button
                    onClick={() => handleAction(row, "approve")}
                    disabled={loading}
                  >
                    {t("approve")}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {emptyRows > 0 && (
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell colSpan={6} />
              </TableRow>
            )}
            {rows.length === 0 && (
              <Box sx={{ padding: "16px" }}>{t("no-result")}</Box>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          labelRowsPerPage={t("rows-per-page")}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
      <RejectDialog />
    </Paper>
  );
}
